// The two reads behind the global suggestions layer (contract 3.2): entity
// matches from GET /api/beta/search/entities and retained archive matches
// from GET /api/beta/evidence-archive/search. Both are disabled until the
// query clears the server's 2 character floor, both keep their previous
// answer while the next one loads so the popover does not blink, and neither
// retries a 4xx (the shared retry rule).
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SEARCH } from '../config';
import { getArchiveSearch, getSearchEntities } from '../endpoints';
import { queryKeys } from '../queryKeys';
import type { ArchiveCallsResponse, SearchEntitiesResponse } from '../types';
import { queryRetry } from './shared';

export function isSearchable(query: string): boolean {
  const trimmed = query.trim();
  return trimmed.length >= SEARCH.minChars && trimmed.length <= SEARCH.maxChars;
}

export interface GlobalSearchOptions {
  readonly enabled?: boolean;
}

export function useSearchEntities(query: string, options: GlobalSearchOptions = {}) {
  const trimmed = query.trim();
  return useQuery<SearchEntitiesResponse>({
    queryKey: queryKeys.searchEntities(trimmed),
    queryFn: ({ signal }) => getSearchEntities(trimmed, SEARCH.entityLimit, signal),
    enabled: (options.enabled ?? true) && isSearchable(trimmed),
    retry: queryRetry,
    placeholderData: keepPreviousData,
  });
}

export function useArchiveSearch(query: string, options: GlobalSearchOptions = {}) {
  const trimmed = query.trim();
  return useQuery<ArchiveCallsResponse>({
    queryKey: queryKeys.archiveSearch(trimmed),
    queryFn: ({ signal }) => getArchiveSearch(trimmed, SEARCH.archiveLimit, signal),
    enabled: (options.enabled ?? true) && isSearchable(trimmed),
    retry: queryRetry,
    placeholderData: keepPreviousData,
  });
}
